/**
 * aiUsageFormat - tokens, durations and run-type names, once.
 *
 * NO MONEY FORMATTER LIVES HERE, deliberately. Every AI-usage surface is
 * customer-facing, and a currency figure on one would be read as a bill —
 * so cost is dropped at the model boundary (`aiUsageModel`) and there is no
 * `formatUsd` left for a render site to reach for. If a price is ever needed
 * for an INTERNAL surface, it belongs in that surface's own module, not in
 * the shared formatter the customer panes import.
 */

/** A token count with thousands separators. */
export const formatTokens = (value: number): string =>
  (Number.isFinite(value) ? Math.round(value) : 0).toLocaleString();

/**
 * A compact token count for a tight cell - "12.4k", "1.2M".
 *
 * Only for places the full number will not fit (a run row's right edge in a
 * 320px pane). Totals and drill-downs get the real figure.
 */
export const formatTokensCompact = (value: number): string => {
  const n = Number.isFinite(value) ? Math.round(value) : 0;
  if (Math.abs(n) < 1000) return String(n);
  if (Math.abs(n) < 1_000_000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  return `${(n / 1_000_000).toFixed(1)}M`;
};

/** A wall-clock duration: "820ms", "4.2s", "3m 05s", "1h 12m". */
export const formatDuration = (ms: number): string => {
  const total = Number.isFinite(ms) && ms > 0 ? ms : 0;
  if (total === 0) return '—';
  if (total < 1000) return `${Math.round(total)}ms`;
  const seconds = total / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${String(Math.floor(seconds % 60)).padStart(2, '0')}s`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
};

/** Byte size for a run's input - "412 KB", "3.1 MB". */
export const formatBytes = (bytes: number): string => {
  const n = Number.isFinite(bytes) && bytes > 0 ? bytes : 0;
  if (n === 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * `model_mix` as one line: "gpt-4o ×12 · o3-mini ×3".
 *
 * Sorted by call count, so the model the run actually leaned on leads. A mix
 * the backend flattened to a bare name (count 0) prints the name alone rather
 * than "×0", which would read as a model that was never called.
 */
export const formatModelMix = (mix: Record<string, number>): string => {
  const entries = Object.entries(mix || {});
  if (!entries.length) return '';
  return entries
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => (count > 0 ? `${name} ×${count}` : name))
    .join(' · ');
};

/**
 * THE FRIENDLY NAME FOR A RUN TYPE.
 *
 * Run types are namespaced identifiers (`matters.generate_more.notes`) and
 * belong nowhere near a reader. The known ones are named explicitly - those
 * strings live in `en.json` and are translated like any other UI text - and
 * anything else is humanised from its own segments rather than printed raw or
 * hidden. An unmapped run type therefore reads as "Agency decisions", not as
 * `matters.generate_more.agency_decisions` and not as "Unknown": the backend
 * will grow run types after this panel ships, and a new one should still land
 * as words.
 *
 * The humanised fallback is deliberately NOT run through `t()`. A key that
 * only exists at runtime cannot be in a locale file, and `t()` on a missing
 * key returns the key - so translating it would buy nothing and would trip
 * the `i18n-check` guard that every referenced key exists in `en.json`.
 */
const RUN_TYPE_LABELS: Record<string, string> = {
  /* Matters - the full pipeline and the per-step top-ups it can re-run.
     The step vocabulary is the backend's ALLOWED_STEPS. */
  'matters.full_analysis': 'Full analysis',
  'matters.generate_more.questions': 'Questions',
  'matters.generate_more.notes': 'Notes',
  'matters.generate_more.tasks': 'Tasks',
  'matters.generate_more.laws': 'Laws',
  'matters.generate_more.cases': 'Cases',
  'matters.generate_more.description': 'Description',
  'matters.generate_more.preparatory_works': 'Preparatory works',
  'matters.generate_more.regulations': 'Regulations',
  'matters.generate_more.agency_decisions': 'Agency decisions',
  'matters.evaluate_source': 'Source evaluation',

  /* Tables */
  'tables.run': 'Table run',
  'tables.extract': 'Extraction',
  'tables.compliance': 'Compliance check',

  /* Drafting */
  'drafts.generate': 'Draft',
  'drafts.regenerate': 'Redraft',

  /* Review */
  'reviews.run': 'Review run',
  'reviews.playbook_extraction': 'Playbook extraction',

  /* The rest of the entity vocabulary */
  'research.query': 'Research query',
  'templates.generate': 'Template',
  'datasources.index': 'Source processing',
};

/** Sentence-cased words from an underscored segment. */
const humaniseSegment = (segment: string): string => {
  const words = segment.replace(/[_-]+/g, ' ').trim();
  if (!words) return '';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Humanise an unmapped run type.
 *
 * The leading segment is the DOMAIN (`matters.`, `tables.`) - which the panel
 * already knows, because it is showing one item's usage - so it is dropped
 * and the rest becomes the name. A single-segment run type keeps its only
 * segment rather than humanising down to an empty string.
 */
export const humaniseRunType = (runType: string): string => {
  const trimmed = (runType || '').trim();
  if (!trimmed) return '';
  const segments = trimmed.split('.').filter(Boolean);
  const meaningful = segments.length > 1 ? segments.slice(1) : segments;
  return meaningful.map(humaniseSegment).filter(Boolean).join(' · ');
};

/** True when the run type has an explicit, translatable name. */
export const isKnownRunType = (runType: string): boolean =>
  Object.prototype.hasOwnProperty.call(RUN_TYPE_LABELS, (runType || '').trim());

/**
 * The English label for a run type, or `null` when there is no mapped one.
 *
 * `null` is the signal to the caller to render `humaniseRunType` DIRECTLY
 * rather than through `t()` - see the note above.
 */
export const runTypeLabelKey = (runType: string): string | null =>
  RUN_TYPE_LABELS[(runType || '').trim()] ?? null;
